import React, { useEffect } from "react";
import { useMediaQuery } from "@material-ui/core";
import { cuideoTheme } from "../../../../containers/themes/defaultTheme";
import { TimeRange, WeeklyCalendarProps } from "./types";
import {
  DAYS,
  WEEKDAY_NIGHT_STARTS,
  WEEKEND_NIGHT_STARTS,
} from "./constants";
import {
  applyNightSchedule,
  clearNightEvents,
  getDayEnabledRange,
  slotIndexToTime,
  timeToSlotIndex,
} from "./utils";
import { NightButton, NightButtonsBar } from "./WeeklyCalendar.styles";
import WeeklyCalendarDesktop from "./WeeklyCalendarDesktop";
import WeeklyCalendarMobile from "./WeeklyCalendarMobile";

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = (props) => {
  const {
    weekdaysEnabled,
    weekendEnabled,
    forceMobile,
    showNightButtons = false,
    value,
    onChange,
  } = props;
  const isDesktop = useMediaQuery(
    `(min-width:${cuideoTheme.breakpoints.values.md}px)`
  );

  useEffect(() => {
    let cleaned = false;
    const newSchedule = { ...value };
    DAYS.forEach((day) => {
      if (!newSchedule[day.key]?.length) return;
      const range = getDayEnabledRange(day, !!weekdaysEnabled, !!weekendEnabled);
      if (!range) {
        delete newSchedule[day.key];
        cleaned = true;
      } else {
        const trimmed = newSchedule[day.key]
          .map(([start, end]: TimeRange): TimeRange | null => {
            const s = Math.max(timeToSlotIndex(start), range.minSlot);
            const e = Math.min(timeToSlotIndex(end), range.maxSlot);
            if (s >= e) return null;
            return [slotIndexToTime(s), slotIndexToTime(e)];
          })
          .filter((r): r is TimeRange => r !== null);
        const original = newSchedule[day.key];
        if (trimmed.length !== original.length || trimmed.some((t, i) => t[0] !== original[i][0] || t[1] !== original[i][1])) {
          if (trimmed.length === 0) {
            delete newSchedule[day.key];
          } else {
            newSchedule[day.key] = trimmed;
          }
          cleaned = true;
        }
      }
    });
    if (cleaned) onChange(newSchedule);
  }, [weekdaysEnabled, weekendEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showNightButtons && (
        <NightButtonsBar>
          <NightButton
            type="button"
            $variant="filled"
            onClick={() =>
              onChange(
                applyNightSchedule(
                  value,
                  WEEKDAY_NIGHT_STARTS,
                  !!weekdaysEnabled,
                  !!weekendEnabled
                )
              )
            }
          >
            Noches entre semana
          </NightButton>
          <NightButton
            type="button"
            $variant="filled"
            onClick={() =>
              onChange(
                applyNightSchedule(
                  value,
                  WEEKEND_NIGHT_STARTS,
                  !!weekdaysEnabled,
                  !!weekendEnabled
                )
              )
            }
          >
            Noches fin de semana
          </NightButton>
          <NightButton
            type="button"
            $variant="outlined"
            onClick={() => onChange(clearNightEvents(value))}
          >
            Limpiar noches
          </NightButton>
        </NightButtonsBar>
      )}
      {isDesktop && !forceMobile ? (
        <WeeklyCalendarDesktop {...props} />
      ) : (
        <WeeklyCalendarMobile {...props} />
      )}
    </>
  );
};

export default WeeklyCalendar;
